import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AttestationCycleRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetAttestationCycleRegisterQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC attestationCycle data to match the GraphQL query structure
 */
const mapTrpcAttestationCyclesToGraphQL = (
  trpcData: AttestationCycleRegisterResponse
): GetAttestationCycleRegisterQuery => {
  return {
    attestation_cycle: trpcData.attestation_cycle.map((cycle) => {
      const document = cycle.document_file?.parent
        ? { ...cycle.document_file?.parent }
        : null;

      const documentFile = cycle.document_file
        ? { ...cycle.document_file, parent: document }
        : null;

      if (!documentFile) {
        throw new Error('Document file is missing in attestation cycle data');
      }

      return {
        ...cycle,
        records: cycle.attestation_record,
        parent: documentFile,
      };
    }),
  };
};

export const useGetAttestationCyclesRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.attestationCycle.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAttestationCyclesToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
